import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Loader2, TrendingUp, Calendar, FileText, MessageCircle, Briefcase, User, Award, ArrowUpRight } from "lucide-react";

const EVENT_META = {
    onboarding: { label: "Ingresso", icon: Award, tone: "emerald" },
    document: { label: "Documento", icon: FileText, tone: "bayside" },
    communication: { label: "Comunicazione", icon: MessageCircle, tone: "magenta" },
    listing: { label: "Mercato", icon: Briefcase, tone: "vermillion" },
    profile_update: { label: "Aggiornamento", icon: User, tone: "bayside" },
    anniversary: { label: "Anniversario", icon: Award, tone: "emerald" },
};

const toneClass = (tone) => ({
    bayside: "text-bayside bg-bayside/10 border-bayside/30",
    magenta: "text-magenta bg-magenta/10 border-magenta/30",
    vermillion: "text-vermillion bg-vermillion/10 border-vermillion/30",
    emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-400/30",
}[tone] || "text-zinc-400 bg-white/5 border-white/10");

export default function Analytics() {
    const [data, setData] = useState(null);

    useEffect(() => {
        apiPrivate.get("/analytics/me").then(r => setData(r.data)).catch(() => {});
    }, []);

    if (!data) {
        return <AppLayout><div className="p-10 text-zinc-500"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Caricamento timeline…</div></AppLayout>;
    }

    const s = data.summary;
    const years = Math.floor(s.days_as_member / 365);
    const months = Math.floor((s.days_as_member % 365) / 30);

    return (
        <AppLayout>
            <div className="max-w-[1400px] mx-auto" data-testid="analytics-page">
                <span className="label-tag">— Il tuo investimento</span>
                <h1 className="font-display text-5xl lg:text-6xl mt-3 tracking-tighter">
                    La tua <span className="font-serif-italic text-gradient-tropical">storia</span> da socio.
                </h1>
                <p className="text-zinc-400 mt-3 max-w-2xl">
                    Una vista d'insieme cronologica della tua partecipazione, dei documenti ricevuti, delle comunicazioni e delle attività sulla Bacheca annunci soci.
                </p>

                {/* KPI grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8" data-testid="analytics-summary">
                    <KpiCard
                        label="Da socio da"
                        value={years > 0 ? `${years}a ${months}m` : `${s.days_as_member} gg`}
                        sub={s.member_since ? new Date(s.member_since).toLocaleDateString("it-IT") : "—"}
                        icon={Calendar}
                        tone="emerald"
                    />
                    <KpiCard
                        label="Numero quote"
                        value={s.valore_nominale_quota ? Number(s.valore_nominale_quota).toLocaleString("it-IT", { maximumFractionDigits: 2 }) : "—"}
                        sub={s.valore_nominale_quota ? "Quote possedute" : ""}
                        icon={TrendingUp}
                        tone="bayside"
                    />
                    <KpiCard
                        label="Documenti totali"
                        value={s.total_documents}
                        sub={s.unread_documents > 0 ? `${s.unread_documents} non letti` : "Tutti letti"}
                        icon={FileText}
                        tone={s.unread_documents > 0 ? "magenta" : "bayside"}
                    />
                    <KpiCard
                        label="Comunicazioni"
                        value={s.total_communications_received}
                        sub="ricevute dal team IR"
                        icon={MessageCircle}
                        tone="magenta"
                    />
                </div>

                {/* Quotas + listings horizontal panel */}
                <div className="bento p-6 mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 items-center" data-testid="analytics-quotes">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">Categorie quota</div>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {s.quota_categories.length === 0 ? (
                                <span className="text-zinc-500 text-sm">—</span>
                            ) : (
                                s.quota_categories.map(q => (
                                    <span key={q} className="text-xs uppercase tracking-widest font-grotesk px-3 py-2 rounded-full border border-bayside bg-bayside/15 text-white">
                                        Quota {q}
                                    </span>
                                ))
                            )}
                        </div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">Inserzioni attive</div>
                        <div className="font-display text-3xl mt-1">{s.active_listings}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">Ultimo aggiornamento dati</div>
                        <div className="font-display text-base mt-2">
                            {s.last_data_update ? new Date(s.last_data_update).toLocaleString("it-IT") : "—"}
                        </div>
                    </div>
                </div>

                {/* Timeline */}
                <div className="mt-12" data-testid="analytics-timeline">
                    <span className="label-tag">— Timeline</span>
                    <h2 className="font-display text-3xl tracking-tight mt-2 mb-6">Cronologia eventi</h2>
                    {data.timeline.length === 0 ? (
                        <div className="bento p-10 text-center text-zinc-500">Nessun evento ancora registrato.</div>
                    ) : (
                        <div className="relative pl-8">
                            <div className="absolute left-3 top-3 bottom-3 w-px bg-white/10" />
                            <div className="space-y-5">
                                {data.timeline.map((e, i) => {
                                    const meta = EVENT_META[e.type] || { label: e.type, icon: TrendingUp, tone: "bayside" };
                                    const Icon = meta.icon;
                                    return (
                                        <TimelineRow key={e.id || `${e.type}-${e.timestamp || e.at || i}`} event={e} meta={meta} Icon={Icon} />
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AppLayout>
    );
}

function KpiCard({ label, value, sub, icon: Icon, tone }) {
    const accent = tone === "emerald" ? "text-emerald-300" : tone === "bayside" ? "text-bayside" : tone === "magenta" ? "text-magenta" : "text-white";
    return (
        <div className="bento p-5">
            <div className="flex items-center justify-between mb-3">
                <Icon className={`h-5 w-5 ${accent}`} />
            </div>
            <div className="font-display text-3xl tracking-tighter">{value}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk mt-2">{label}</div>
            {sub && <div className="text-xs text-zinc-400 mt-1 font-grotesk">{sub}</div>}
        </div>
    );
}

function TimelineRow({ event, meta, Icon }) {
    const content = (
        <div className="relative">
            <div className={`absolute -left-8 top-1 h-6 w-6 rounded-full flex items-center justify-center border ${toneClass(meta.tone)}`}>
                <Icon className="h-3 w-3" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk">
                {new Date(event.timestamp).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}
            </div>
            <div className="flex items-center gap-2 mt-1">
                <span className={`text-[9px] font-bold uppercase tracking-widest font-grotesk px-2 py-0.5 rounded-full border ${toneClass(meta.tone)}`}>
                    {meta.label}
                </span>
                <h3 className="font-display text-base leading-tight">{event.title}</h3>
                {event.href && <ArrowUpRight className="h-3.5 w-3.5 text-zinc-600" />}
            </div>
            {event.description && <p className="text-zinc-400 text-sm mt-1.5">{event.description}</p>}
        </div>
    );
    return event.href ? (
        <a href={event.href} className="block hover:opacity-90 transition" data-testid={`timeline-${event.type}`}>{content}</a>
    ) : (
        <div data-testid={`timeline-${event.type}`}>{content}</div>
    );
}
